import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useSelect } from 'downshift';
import classNames from 'classnames';
import { faFilter, faSquareCheck } from '@fortawesome/pro-solid-svg-icons';
import { faSquare } from '@fortawesome/pro-regular-svg-icons';
import { Icon } from 'js/components';
import { computeAnchoredOverlayOffset } from 'js/utils/anchored-overlay';
import styles from './SeriesRadarChart.module.scss';

function itemToString(item) {
  return item?.name ?? '';
}

export default function SeriesRadarLegendFilter({
  items = [],
  selectedKeys = [],
  maxSeries = 0,
  onChange,
}) {
  const selectedSet = useMemo(() => new Set(selectedKeys), [selectedKeys]);
  const atLimit = maxSeries > 0 && selectedKeys.length >= maxSeries;
  const showMaxMessage = maxSeries > 0 && items.length > maxSeries;
  const allSelected =
    items.length > 0 && items.every((item) => selectedSet.has(item.key));
  const menuRef = useRef(null);
  const [menuOffset, setMenuOffset] = useState({
    left: 0,
    maxWidth: undefined,
  });

  const {
    isOpen,
    getToggleButtonProps,
    getMenuProps,
    highlightedIndex,
    getItemProps,
  } = useSelect({
    items,
    selectedItem: null,
    itemToString,
    isItemDisabled: (item) => atLimit && !selectedSet.has(item?.key),
    stateReducer: (state, actionAndChanges) => {
      const { type, changes } = actionAndChanges;
      switch (type) {
        case useSelect.stateChangeTypes.ItemClick:
          return {
            ...changes,
            isOpen: true,
            highlightedIndex: state.highlightedIndex,
          };
        default:
          return changes;
      }
    },
    onSelectedItemChange: ({ selectedItem }) => {
      if (!selectedItem) {
        return;
      }
      const isSelected = selectedSet.has(selectedItem.key);
      if (isSelected) {
        onChange(selectedKeys.filter((key) => key !== selectedItem.key));
        return;
      }
      if (atLimit) {
        return;
      }
      onChange([...selectedKeys, selectedItem.key]);
    },
  });

  useLayoutEffect(() => {
    if (!isOpen) {
      setMenuOffset({ left: 0, maxWidth: undefined });
      return;
    }
    const menu = menuRef.current;
    const trigger = menu?.offsetParent;
    if (!menu || !trigger) {
      return;
    }
    const next = computeAnchoredOverlayOffset({
      triggerRect: trigger.getBoundingClientRect(),
      overlayWidth: menu.getBoundingClientRect().width,
      viewportWidth: window.innerWidth,
    });
    setMenuOffset((current) => {
      if (current.left === next.left && current.maxWidth === next.maxWidth) {
        return current;
      }
      return { left: next.left, maxWidth: next.maxWidth };
    });
  }, [isOpen, items.length]);

  return (
    <div className={classNames('combobox', styles.filter)}>
      <button
        {...getToggleButtonProps({
          type: 'button',
          className: classNames('btn btn-panel-control', styles.filterButton, {
            'btn-panel-control-open': isOpen,
            'btn-panel-control-active': !allSelected,
          }),
          'aria-label': 'Filter series',
          title: 'Filter series',
        })}
      >
        <Icon icon={faFilter} fixedWidth />
      </button>
      <ul
        {...getMenuProps({ ref: menuRef })}
        className={classNames('combobox-items', styles.filterMenu)}
        style={
          isOpen
            ? {
                left: menuOffset.left,
                right: 'auto',
                maxWidth: menuOffset.maxWidth,
              }
            : { display: 'none' }
        }
      >
        {isOpen ? (
          <>
            {showMaxMessage ? (
              <li className="combobox-plain-item">Select up to {maxSeries}</li>
            ) : null}
            {items.map((item, index) => {
              const isSelected = selectedSet.has(item.key);
              const isDisabled = atLimit && !isSelected;
              return (
                <li
                  key={item.key}
                  className={classNames('combobox-item', {
                    highlighted: highlightedIndex === index,
                    [styles.filterItemDisabled]: isDisabled,
                  })}
                  {...getItemProps({ item, index, disabled: isDisabled })}
                >
                  <span className={styles.filterItemCheck}>
                    <Icon
                      className="icon"
                      icon={isSelected ? faSquareCheck : faSquare}
                      size="xl"
                      fixedWidth
                    />
                  </span>
                  <span className={styles.filterItemName}>{item.name}</span>
                </li>
              );
            })}
          </>
        ) : null}
      </ul>
    </div>
  );
}
